/**
 * File Upload Preview Renderer
 * Canvas-based mask renderer for shape-aware preview generation
 */

class FileUploadPreviewRenderer {
  constructor() {
    this.maskCache = new Map();
    this.debug = window.PITCHPRINT_DEBUG || false;
  }

  log(...args) {
    if (this.debug) console.log('[PreviewRenderer]', ...args);
  }

  /**
   * Get mask URL for shape
   */
  getMaskUrl(shape) {
    // Use URLs injected by Liquid template if available
    if (window.FILE_UPLOAD_MASK_URLS && window.FILE_UPLOAD_MASK_URLS[shape]) {
      return window.FILE_UPLOAD_MASK_URLS[shape];
    }

    // Fallback to static paths (shouldn't be used in production)
    const maskMap = {
      'Circle Shape': '/assets/circle-overlay.png',
      'Classic Shape': '/assets/classic-overlay.png',
      'Longboard Shape': '/assets/longboard-overlay.png',
      'Longboard Mini': '/assets/longboard-mini-overlay.png'
    };

    return maskMap[shape] || maskMap['Classic Shape'];
  }

  /**
   * Load mask image with caching
   */
  async loadMask(shape) {
    // Check cache
    if (this.maskCache.has(shape)) {
      this.log('Using cached mask for', shape);
      return this.maskCache.get(shape);
    }

    const maskUrl = this.getMaskUrl(shape);
    this.log('Loading mask:', maskUrl);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        this.maskCache.set(shape, img);
        this.log('Mask loaded:', shape, `${img.width}x${img.height}`);
        resolve(img);
      };

      img.onerror = (error) => {
        console.error('[PreviewRenderer] Failed to load mask:', maskUrl, error);
        reject(new Error(`Failed to load mask: ${maskUrl}`));
      };

      img.src = maskUrl;
    });
  }

  /**
   * Load image from File object (supports PNG, JPG, and PDF)
   */
  async loadImage(file) {
    this.log('Loading image file:', file.name);

    // Check if it's a PDF
    if (file.type === 'application/pdf') {
      return this.loadPDFAsImage(file);
    }

    // Handle regular images (PNG, JPG)
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          this.log('Image loaded:', `${img.width}x${img.height}`);
          resolve(img);
        };

        img.onerror = (error) => {
          console.error('[PreviewRenderer] Failed to load image:', error);
          reject(new Error('Failed to load image'));
        };

        img.src = e.target.result;
      };

      reader.onerror = (error) => {
        console.error('[PreviewRenderer] Failed to read file:', error);
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Load PDF and render first page as image
   */
  async loadPDFAsImage(file) {
    this.log('Loading PDF file:', file.name);

    // Check if PDF.js is loaded
    if (typeof pdfjsLib === 'undefined') {
      console.error('[PreviewRenderer] PDF.js library not loaded');
      throw new Error('PDF.js library required for PDF preview. Please add PDF.js CDN link to theme.');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          // Load PDF document
          const pdf = await pdfjsLib.getDocument({ data: e.target.result }).promise;
          this.log('PDF loaded:', pdf.numPages, 'pages');

          // Get first page
          const page = await pdf.getPage(1);
          this.log('PDF page loaded');

          // Intelligent adaptive scaling based on PDF dimensions and file size
          // Goal: Balance quality (target 4000-6000px width) with performance (prevent UI blocking)

          const baseViewport = page.getViewport({ scale: 1.0 });
          const pdfWidth = baseViewport.width;
          const pdfHeight = baseViewport.height;
          const fileSizeMB = file.size / (1024 * 1024);

          this.log(`PDF dimensions at 1.0x: ${pdfWidth}x${pdfHeight}, file size: ${fileSizeMB.toFixed(2)}MB`);

          // Calculate scale to achieve ~5000px width for quality, but cap based on file size
          let targetScale = 5000 / pdfWidth;

          // Apply file size limits to prevent memory issues
          if (fileSizeMB > 100) {
            targetScale = Math.min(targetScale, 1.0); // Very large files: max 1.0x
          } else if (fileSizeMB > 50) {
            targetScale = Math.min(targetScale, 1.5); // Large files: max 1.5x
          } else {
            targetScale = Math.min(targetScale, 2.0); // Small files: max 2.0x
          }

          // Ensure minimum scale of 0.5x
          const scale = Math.max(0.5, targetScale);

          this.log(`Using intelligent scale ${scale.toFixed(2)} for ${fileSizeMB.toFixed(2)}MB PDF (target: quality with performance)`);

          const viewport = page.getViewport({ scale });

          // Create canvas to render PDF
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          // Render PDF page to canvas
          // For large files, yield to browser between rendering to prevent UI blocking
          const renderTask = page.render({
            canvasContext: context,
            viewport: viewport
          });

          await renderTask.promise;

          this.log('PDF rendered to canvas:', `${canvas.width}x${canvas.height}`);

          // Save raw rasterized blob for server-side cropping (before any mask is applied)
          // This high-res PNG allows process-image.js to generate cropped URLs for PDF uploads
          // Stored as a promise so callers can await it (canvas.toBlob is callback-based)
          this._pdfRasterizedBlobPromise = new Promise((resolveBlob) => {
            canvas.toBlob((blob) => {
              this._lastPdfRasterizedBlob = blob;
              this.log('PDF rasterized blob saved:', blob ? `${(blob.size / 1024 / 1024).toFixed(1)}MB` : 'null');
              resolveBlob(blob);
            }, 'image/png', 0.95);
          });

          // Convert canvas to image for preview rendering
          const img = new Image();
          img.onload = () => {
            this.log('PDF converted to image');
            resolve(img);
          };
          img.onerror = (error) => {
            console.error('[PreviewRenderer] Failed to convert PDF canvas to image:', error);
            reject(new Error('Failed to convert PDF to image'));
          };
          img.src = canvas.toDataURL('image/png');

        } catch (error) {
          console.error('[PreviewRenderer] Failed to process PDF:', error);
          reject(new Error('Failed to process PDF: ' + error.message));
        }
      };

      reader.onerror = (error) => {
        console.error('[PreviewRenderer] Failed to read PDF file:', error);
        reject(new Error('Failed to read PDF file'));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Generate a dynamic canvas mask with per-corner radii
   * Used for custom product shapes defined via metafield config
   * @param {number} width - Canvas width in pixels
   * @param {number} height - Canvas height in pixels
   * @param {object} corners - Per-corner radii as fraction of shorter dimension { tl, tr, bl, br }
   * @returns {HTMLCanvasElement} Canvas element usable as mask
   */
  generateDynamicMask(width, height, corners = {}) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;

    // Convert corner percentages to pixel values based on shorter dimension
    const base = Math.min(width, height);
    const tl = (corners.tl || 0) * base;
    const tr = (corners.tr || 0) * base;
    const br = (corners.br || 0) * base;
    const bl = (corners.bl || 0) * base;

    // Draw rounded rect path with independent corner radii
    ctx.beginPath();
    ctx.moveTo(tl, 0);
    ctx.lineTo(width - tr, 0);
    if (tr > 0) {
      ctx.arcTo(width, 0, width, tr, tr);
    } else {
      ctx.lineTo(width, 0);
    }
    ctx.lineTo(width, height - br);
    if (br > 0) {
      ctx.arcTo(width, height, width - br, height, br);
    } else {
      ctx.lineTo(width, height);
    }
    ctx.lineTo(bl, height);
    if (bl > 0) {
      ctx.arcTo(0, height, 0, height - bl, bl);
    } else {
      ctx.lineTo(0, height);
    }
    ctx.lineTo(0, tl);
    if (tl > 0) {
      ctx.arcTo(0, 0, tl, 0, tl);
    } else {
      ctx.lineTo(0, 0);
    }
    ctx.closePath();

    ctx.fillStyle = 'white';
    ctx.fill();

    this.log('Dynamic mask generated:', `${width}x${height}`, 'corners:', corners);
    return canvas;
  }

  /**
   * Draw image to fill entire mask shape (cover behavior)
   * Scales artwork proportionately to fill mask completely and crops excess
   */
  drawCoveredImage(ctx, img, targetWidth, targetHeight) {
    const imgRatio = img.width / img.height;
    const targetRatio = targetWidth / targetHeight;

    let drawWidth, drawHeight, offsetX = 0, offsetY = 0;

    if (imgRatio > targetRatio) {
      // Image wider than target - scale to height (fill height, crop sides)
      drawHeight = targetHeight;
      drawWidth = img.width * (targetHeight / img.height);
      offsetX = (targetWidth - drawWidth) / 2;
    } else {
      // Image taller/equal to target - scale to width (fill width, crop top/bottom)
      drawWidth = targetWidth;
      drawHeight = img.height * (targetWidth / img.width);
      offsetY = (targetHeight - drawHeight) / 2;
    }

    // Clear canvas
    ctx.clearRect(0, 0, targetWidth, targetHeight);

    // Draw image to fill entire canvas, cropping excess
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

    this.log('Drew covered image:', `${drawWidth.toFixed(0)}x${drawHeight.toFixed(0)} at (${offsetX.toFixed(0)}, ${offsetY.toFixed(0)})`);
  }

  /**
   * Generate masked preview from uploaded file
   * @param {File} uploadedFile - The file to generate preview from
   * @param {string} shape - The shape mask to apply ("Custom" uses customConfig)
   * @param {number|object} maxDimensionOrConfig - Max dimension (number) or options object
   *   When object: { maxDimension, customConfig: { width, height, corners: { tl, tr, bl, br } } }
   */
  async generatePreview(uploadedFile, shape, maxDimensionOrConfig = 1200) {
    try {
      // Parse arguments: support both old signature (number) and new (object)
      let maxDimension = 1200;
      let customConfig = null;

      if (typeof maxDimensionOrConfig === 'object' && maxDimensionOrConfig !== null) {
        maxDimension = maxDimensionOrConfig.maxDimension || 1200;
        customConfig = maxDimensionOrConfig.customConfig || null;
      } else {
        maxDimension = maxDimensionOrConfig;
      }

      this.log('Generating preview for:', uploadedFile.name, 'Shape:', shape, customConfig ? 'Custom config:' : '', customConfig || '');

      let maskCanvas;
      let canvasWidth, canvasHeight;

      if (shape === 'Custom' && customConfig) {
        // Dynamic mask from custom config
        const uploadedImg = await this.loadImage(uploadedFile);

        // Calculate canvas dimensions from config aspect ratio
        const aspectRatio = customConfig.width / customConfig.height;
        if (aspectRatio >= 1) {
          // Landscape or square
          canvasWidth = maxDimension;
          canvasHeight = Math.floor(maxDimension / aspectRatio);
        } else {
          // Portrait
          canvasHeight = maxDimension;
          canvasWidth = Math.floor(maxDimension * aspectRatio);
        }

        // Generate dynamic mask with per-corner radii
        maskCanvas = this.generateDynamicMask(canvasWidth, canvasHeight, customConfig.corners || {});

        // Create main canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: false, alpha: true });
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        this.log('Custom canvas size:', `${canvasWidth}x${canvasHeight}`, 'ratio:', aspectRatio.toFixed(3));

        // Draw uploaded image to cover canvas
        this.drawCoveredImage(ctx, uploadedImg, canvasWidth, canvasHeight);

        // Apply dynamic mask
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(maskCanvas, 0, 0);

        this.log('Custom preview generated successfully');

        const dataUrl = canvas.toDataURL('image/png', 1.0);

        return new Promise((resolve) => {
          canvas.toBlob((blob) => {
            resolve({
              dataUrl,
              blob,
              width: canvasWidth,
              height: canvasHeight,
              shape,
              fileName: uploadedFile.name
            });
          }, 'image/png', 0.95);
        });
      }

      // Standard shape flow (unchanged) - load PNG mask and image in parallel
      const [maskImg, uploadedImg] = await Promise.all([
        this.loadMask(shape),
        this.loadImage(uploadedFile)
      ]);

      // Create canvas with mask dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', {
        willReadFrequently: false,
        alpha: true
      });

      canvasWidth = maskImg.width;
      canvasHeight = maskImg.height;

      if (Math.max(canvasWidth, canvasHeight) > maxDimension) {
        const scale = maxDimension / Math.max(canvasWidth, canvasHeight);
        canvasWidth = Math.floor(canvasWidth * scale);
        canvasHeight = Math.floor(canvasHeight * scale);
      }

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      this.log('Canvas size:', `${canvasWidth}x${canvasHeight}`);

      // Step 1: Draw uploaded image to cover entire canvas (fill and crop)
      this.drawCoveredImage(ctx, uploadedImg, canvasWidth, canvasHeight);

      // Step 2: Create temporary canvas for scaled mask
      maskCanvas = document.createElement('canvas');
      const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: false });
      maskCanvas.width = canvasWidth;
      maskCanvas.height = canvasHeight;

      // Draw scaled mask
      maskCtx.drawImage(maskImg, 0, 0, canvasWidth, canvasHeight);

      // Step 3: Apply mask using destination-in composite
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(maskCanvas, 0, 0);

      this.log('Preview generated successfully');

      const dataUrl = canvas.toDataURL('image/png', 1.0);

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve({
            dataUrl,
            blob,
            width: canvasWidth,
            height: canvasHeight,
            shape,
            fileName: uploadedFile.name
          });
        }, 'image/png', 0.95);
      });

    } catch (error) {
      console.error('[PreviewRenderer] Error generating preview:', error);
      throw error;
    }
  }

  /**
   * Generate thumbnail from preview
   */
  async generateThumbnail(previewDataUrl, maxSize = 200) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate thumbnail dimensions
        const scale = maxSize / Math.max(img.width, img.height);
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);

        // Draw thumbnail
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL('image/png', 0.8));
      };

      img.onerror = reject;
      img.src = previewDataUrl;
    });
  }

  /**
   * Generate raw thumbnail from uploaded file (no mask applied)
   * Used as reference to show original artwork without scaling/masking
   */
  async generateRawThumbnail(uploadedFile, maxSize = 200) {
    try {
      this.log('Generating raw thumbnail for:', uploadedFile.name);

      // Load the original image
      const uploadedImg = await this.loadImage(uploadedFile);

      // Create canvas with thumbnail dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate thumbnail dimensions maintaining aspect ratio
      const scale = maxSize / Math.max(uploadedImg.width, uploadedImg.height);
      canvas.width = Math.floor(uploadedImg.width * scale);
      canvas.height = Math.floor(uploadedImg.height * scale);

      // Draw scaled down image (no mask, no cropping)
      ctx.drawImage(uploadedImg, 0, 0, canvas.width, canvas.height);

      this.log('Raw thumbnail generated:', `${canvas.width}x${canvas.height}`);

      const dataUrl = canvas.toDataURL('image/png', 0.8);

      // Return blob for S3 upload (needed for PDF preview in cart)
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve({
            dataUrl,
            blob,
            width: canvas.width,
            height: canvas.height
          });
        }, 'image/png', 0.95);
      });

    } catch (error) {
      console.error('[PreviewRenderer] Error generating raw thumbnail:', error);
      throw error;
    }
  }

  /**
   * Clear mask cache
   */
  clearCache() {
    this.maskCache.clear();
    this.log('Mask cache cleared');
  }
}

// Export for use in other scripts
window.FileUploadPreviewRenderer = FileUploadPreviewRenderer;
